import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useRoomContext } from '../../context/RoomContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { useI18n } from '../../context/I18nContext';
import logger from '../../utils/logger';
import EditHistoryMessage from './EditHistoryMessage';
import './EditHistory.css';
import RightSidebar from './RightSidebar';

const EditHistory = ({ message, onClose = () => {} }) => {
  const messageId = message?.getId() || 'N/A';
  const { roomId } = useRoomContext();
  const client = useMatrixClient();
  const { t } = useI18n();
  const [editEvents, setEditEvents] = useState(null);
  const [originalEvent, setOriginalEvent] = useState(null);

  useEffect(() => {
    const fetchEditHistory = async () => {
      if (client && roomId && message && message.getId) {
        try {
          let allEvents = [];
          let nextBatch = undefined;
          let originalEvent = message;
          
          do {
            const opts = nextBatch ? { from: nextBatch } : undefined;
            const result = await client.relations(
              roomId,
              message.getId(),
              'm.replace',
              'm.room.message',
              opts,
            );
            if (result.events) {
              allEvents = allEvents.concat(result.events);
            }
            if (result.originalEvent) {
              originalEvent = result.originalEvent;
            }
            nextBatch = result.nextBatch;
          } while (nextBatch);
          setEditEvents(allEvents);
          setOriginalEvent(originalEvent);
        } catch (err) {
          logger.error('Failed to fetch edit history', err);
        }
      }
    };
    fetchEditHistory();
  }, [client, roomId, message, messageId]);

  if (editEvents === null) {
    // Still loading
    return null;
  }
  
  return (
    <RightSidebar
      isOpen={true}
      onClose={onClose}
      title={t('EditHistory.title')}
      className="edit-history-sidebar"
    >
      {editEvents.length === 0 ? (
        <p style={{ textAlign: 'center', margin: '32px 0' }}>{t('EditHistory.noEditsFound')}</p>
      ) : (
        (() => {
          // Group events by date string (e.g., 'Thu, Oct 9, 2025')
          const groups = {};
          editEvents.forEach((event, idx) => {
            const ts = event.getTs ? event.getTs() : null;
            const date = ts ? new Date(ts) : null;
            let dateStr = t('EditHistory.unknownDate');
            if (date) {
              const today = new Date();
              const yesterday = new Date();
              yesterday.setDate(today.getDate() - 1);
              // Compare only year, month, day
              const isToday = date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
              const isYesterday = date.getFullYear() === yesterday.getFullYear() && date.getMonth() === yesterday.getMonth() && date.getDate() === yesterday.getDate();
              if (isToday) {
                dateStr = t('EditHistory.today');
              } else if (isYesterday) {
                dateStr = t('EditHistory.yesterday');
              } else {
                dateStr = date.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
              }
            }
            if (!groups[dateStr]) groups[dateStr] = [];
            groups[dateStr].push({ event, idx });
          });
          // Render groups with divider
          // Add the original message (no previousEdit) at the bottom of the earliest date divider group
          const groupEntries = Object.entries(groups);
          if (groupEntries.length === 0) return null;
          // Find the earliest date group
          const firstDate = groupEntries[groupEntries.length - 1][0];
          const result = groupEntries.map(([dateStr, items]) => {
            const isFirst = dateStr === firstDate;
            return [
              <div key={dateStr} className="edit-history-date-divider">{dateStr}</div>,
              ...items.map(({ event, idx }) => (
                <EditHistoryMessage
                  key={event.getId ? event.getId() : idx}
                  previousEdit={
                    idx === editEvents.length - 1
                      ? originalEvent
                      : editEvents[idx + 1]
                  }
                  currentEdit={event}
                  isOriginal={idx === editEvents.length - 1}
                  timestamp={event.getTs ? event.getTs() : null}
                  timeOnly={true}
                />
              )),
              ...(isFirst
                ? [
                    <EditHistoryMessage
                      key="original"
                      previousEdit={undefined}
                      currentEdit={originalEvent}
                      isOriginal={true}
                      timestamp={originalEvent && originalEvent.getTs ? originalEvent.getTs() : null}
                      timeOnly={true}
                    />,
                  ]
                : []),
            ];
          });
          return result;
        })()
      )}
    </RightSidebar>
  );
};

EditHistory.propTypes = {
  message: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default EditHistory;